import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useSectionNav } from "@/lib/useSectionNav";
import wordmarkWhite from "@/assets/echo-bravo-wordmark-white.png";
import wordmarkNavy from "@/assets/echo-bravo-wordmark-navy.png";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const goNav = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    goNav(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-silver-200 shadow-[0_8px_30px_rgba(10,17,40,0.06)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <button
          data-testid="navbar-logo"
          onClick={() => go("#top")}
          className="flex items-center"
          aria-label="Echo Bravo home"
        >
          <img
            src={scrolled ? wordmarkNavy : wordmarkWhite}
            alt="Echo Bravo"
            className="h-7 sm:h-8 w-auto transition-opacity duration-300"
          />
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className={`text-sm font-semibold tracking-tight transition-colors duration-200 ${
                scrolled
                  ? "text-navy-700 hover:text-brand-blue"
                  : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="navbar-cta"
            onClick={() => go("#contact")}
            className="hidden sm:inline-flex items-center rounded-sm bg-brand-blue px-5 py-2.5 text-sm font-bold text-white transition-all duration-300 hover:bg-navy-700 hover:-translate-y-0.5"
          >
            Request a Consultation
          </button>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm ${
              scrolled ? "text-navy-900" : "text-white"
            }`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden bg-navy-900 border-t border-white/10 px-5 py-6 space-y-1"
        >
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="block w-full text-left py-3 text-base font-semibold text-white/90 hover:text-white border-b border-white/5"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-navbar-cta"
            onClick={() => go("#contact")}
            className="mt-4 w-full rounded-sm bg-brand-blue px-5 py-3 text-sm font-bold text-white"
          >
            Request a Consultation
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
